/**
 * Swaps the bundle hero's banner image for a looping video, with a play/pause control.
 *
 * The image is not removed — it becomes the video's poster, so the first paint is the frame
 * shoppers already saw rather than a black box, and a device that refuses the video keeps a
 * hero instead of a gap.
 *
 * Autoplay only happens on the terms browsers actually allow: muted, inline, and initiated
 * from a play() whose rejection is caught. An unmuted or non-inline autoplay is refused on
 * iOS outright, and a rejected play() that nobody catches leaves an unhandled rejection and
 * a hero stuck on its poster with no way to start it — hence the control below.
 */

export const VIDEO_URL = "https://tbimg.b-cdn.net/vault/vault_chase.mp4";
export const VIDEO_CLASS = "tbHeroVideo";
export const TOGGLE_CLASS = "tbHeroToggle";

/**
 * The eyebrow above the headline. The app renders "Free shipping" here from the bundle's
 * own settings, so this overwrites it in the DOM.
 *
 * That makes this the one piece of hero copy that lives in the theme rather than in the
 * Easy Bundles admin, which is worth knowing before someone edits the bundle and wonders
 * why the page ignores them. The durable home is that admin field; this is the only lever
 * reachable without a person pasting into the app.
 */
export const EYEBROW_TEXT = "SUMMER VAULT NOW OPEN";

const PLAY_PATH = "M8 5v14l11-7z";
const PAUSE_PATH = "M7 5h3.5v14H7zM13.5 5H17v14h-3.5z";

/**
 * Whether the hero should start playing by itself.
 *
 * Reduced motion is a real preference, not a nicety: a 17-second loop behind the page is
 * exactly the kind of motion the setting exists to stop. The video still loads and the
 * control still starts it — the choice just moves to the shopper.
 */
export function shouldAutoplay(win = window) {
  return !win.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
}

/** The label and glyph for a given playing state. */
export function toggleState(playing) {
  return playing
    ? { label: "Pause video", path: PAUSE_PATH }
    : { label: "Play video", path: PLAY_PATH };
}

function paint(button, playing) {
  const { label, path } = toggleState(playing);
  button.setAttribute("aria-label", label);
  button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${path}" fill="currentColor"/></svg>`;
}

export function mount(doc = document, win = window) {
  const header = doc.querySelector(".gbbBundleHeader");
  if (!header || header.querySelector(`.${VIDEO_CLASS}`)) return null;

  const banner = header.querySelector("img.gbbBundleBanner");

  const video = doc.createElement("video");
  video.className = VIDEO_CLASS;
  video.src = VIDEO_URL;
  if (banner?.currentSrc || banner?.src) video.poster = banner.currentSrc || banner.src;
  video.loop = true;
  video.muted = true; // the property, not only the attribute — the attribute alone does not satisfy autoplay policy
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute("muted", "");
  video.setAttribute("playsinline", "");
  video.setAttribute("aria-label", "Summer Vault");
  video.preload = "metadata";

  const button = doc.createElement("button");
  button.type = "button";
  button.className = TOGGLE_CLASS;

  // Before the copy overlay, so the absolutely-positioned headline still paints on top.
  header.insertBefore(video, header.firstChild);
  header.appendChild(button);

  // The image stays in the DOM as the poster's source but must not also render.
  if (banner) banner.style.display = "none";

  const sync = () => paint(button, !video.paused && !video.ended);
  video.addEventListener("play", sync);
  video.addEventListener("pause", sync);

  button.addEventListener("click", () => {
    if (video.paused) video.play().catch(sync);
    else video.pause();
  });

  if (shouldAutoplay(win)) video.play().catch(sync);
  sync();
  return video;
}

/**
 * The eyebrow, re-asserted. Guarded on the current value so writing it cannot re-enter the
 * observer that calls this.
 */
function ensureEyebrow(doc) {
  const eyebrow = doc.querySelector(".gbbBundleHeaderCopy p");
  if (eyebrow && eyebrow.textContent.trim() !== EYEBROW_TEXT)
    eyebrow.textContent = EYEBROW_TEXT;
}

/**
 * Waits for the header, then keeps the hero as this file wants it.
 *
 * TWO things were wrong with the obvious version, both found on the live page rather than
 * reasoned about. A one-shot run does nothing at all: .gbbBundleHeader is built by the app
 * well after DOMContentLoaded, so the script found no header and gave up. And disconnecting
 * once the video mounts loses the copy: the app re-renders .gbbBundleHeaderCopy afterwards,
 * which put "Free shipping" back while the video stayed put — the video is markup this file
 * owns, the eyebrow is markup it only overwrites.
 *
 * So the observer stays for the life of the page, exactly as assets/tb-card-title.js does.
 * Both calls below are no-ops once satisfied, so the observer settles rather than looping.
 */
function start(doc = document) {
  const run = () => {
    mount(doc);
    ensureEyebrow(doc);
  };
  run();
  new MutationObserver(run).observe(doc.body, {
    childList: true,
    subtree: true,
  });
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => start(), { once: true });
  else start();
}
